import React, { Component } from 'react'
import { Button, Col, Form, Input, Modal, notification, Popconfirm, Row, Select, Space, Spin, Table, Tabs, Typography } from 'antd'
import { PricingAPI } from '../../../controller/PricingAPI';
import InvitationPanel from './InvitationPanel';

// 與後端 config/permissions.php 的角色代碼一致
const ROLE_OPTIONS = [
    { value: 'admin', label: '管理員' },
    { value: 'sales', label: '業務' },
];

const ROLE_LABELS = ROLE_OPTIONS.reduce(
    (labels, option) => ({ ...labels, [option.value]: option.label }),
    {}
);

// last_active_at 在從未登入的帳號上是 null，轉成 0 當作最早，
// 避免 new Date(null/undefined) 產生 NaN 讓整個 sorter 失效
const activeTime = (value) => (value ? new Date(value).getTime() : 0);

export class UserPage extends Component {
    form = React.createRef();
    state = {
        results: [],
        loading: true,
        modalVisible: false,
        modalType: 'store',
        modalId: null,
        modalLoading: false,
        departmentOptions: [],
    }
    componentDidMount() {
        this.refresh();
        PricingAPI.departments.options().then((response) => {
            this.setState({ departmentOptions: response.data.map((d) => ({ value: d.id, label: d.name })) });
        }).catch((error) => {
            notification.error({ message: '載入部門清單失敗', description: this.errorMessage(error) });
        });
    }
    refresh = () => {
        this.setState({ loading: true });
        PricingAPI.users.index({}, 1, 999).then((response) => {
            const result = response.data;
            this.setState({ results: result.data });
        }).finally(() => {
            this.setState({ loading: false });
        });
    }
    showAddModal = () => {
        this.setState({
            modalVisible: true,
            modalType: 'store',
        }, () => {
            this.form.current.setFieldsValue({
                name: null,
                email: null,
                role: 'sales',
                department_ids: [],
                password: null,
            });
        });
    }
    showUpdateModal = (id) => {
        this.setState({
            modalLoading: true,
            modalVisible: true,
            modalType: 'update',
            modalId: id,
        });
        PricingAPI.users.show(id).then((response) => {
            const result = response.data;
            this.form.current.setFieldsValue(result);
        }).finally(() => {
            this.setState({ modalLoading: false });
        });
    }
    hideModal = () => {
        // reset state;
        this.setState({
            modalLoading: false,
            modalVisible: false,
            modalType: 'store',
            modalId: null,
        });
        this.form.current.setFieldsValue({
            name: null,
            email: null,
            role: null,
            department_ids: [],
            password: null,
        });
    }
    handleOk = () => {
        // 不在這裡關閉對話框：驗證失敗時 onFinish 不會執行，
        // 先關掉會讓使用者看不到欄位錯誤，編輯也靜默消失。
        // 成功時由 handleFinish 的 then 關閉；失敗時刻意不關，
        // 讓使用者留在對話框內看見錯誤訊息並保留已輸入的內容。
        this.form.current.submit();
    }
    // Laravel 驗證失敗回傳的 data.message 一律是制式的
    // "The given data was invalid."，真正有用的欄位訊息在 data.errors 裡，
    // 取第一筆顯示給使用者。
    errorMessage = (error) => {
        const data = error?.response?.data;
        const errors = data?.errors;
        if (errors) {
            const firstMessages = Object.values(errors)[0];
            if (Array.isArray(firstMessages) && firstMessages.length > 0) {
                return firstMessages[0];
            }
        }
        return data?.message || '請確認欄位內容';
    }
    handleFinish = (values) => {
        this.setState({ loading: true });
        if (this.state.modalType === 'store') {
            // store
            PricingAPI.users.store(values).then((response) => {
                notification.info({ message: '新增成功' });
                this.refresh();
                this.hideModal();
            }).catch((error) => {
                notification.error({
                    message: '新增失敗',
                    description: this.errorMessage(error),
                });
            }).finally(() => {
                this.setState({ loading: false });
            });
        } else {
            // update
            PricingAPI.users.update(this.state.modalId, values).then((response) => {
                notification.info({ message: '修改成功' });
                this.refresh();
                this.hideModal();
            }).catch((error) => {
                notification.error({
                    message: '修改失敗',
                    description: this.errorMessage(error),
                });
            }).finally(() => {
                this.setState({ loading: false });
            });
        }
    }
    handleDelete = (id) => {
        this.setState({ loading: true });
        PricingAPI.users.destroy(id).then((response) => {
            notification.info({ message: '刪除成功' });
            this.refresh();
        }).catch((error) => {
            notification.info({ message: '刪除失敗' });
        }).finally(() => {
            this.setState({ loading: false });
        });
    }
    render() {
        const columns = [
            {
                title: '名稱',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'E-mail',
                dataIndex: 'email',
                key: 'email',
            },
            {
                title: '角色',
                dataIndex: 'role',
                key: 'role',
                render: (value) => ROLE_LABELS[value] || value,
            },
            {
                // 取自 Sanctum 每個請求更新的 last_used_at，是「最後一次用系統」的時間，
                // 與下一欄的「帳號更新於」(帳號資料被修改的時間) 不同，兩者不可混淆
                title: '最後活動',
                dataIndex: 'last_active_at',
                key: 'last_active_at',
                sorter: (a, b) => activeTime(a.last_active_at) - activeTime(b.last_active_at),
                render: (value) => (value ? new Date(value).toLocaleString() : '從未'),
            },
            {
                title: '帳號更新於',
                dataIndex: 'updated_at',
                key: 'updated_at',
                render: (value) => new Date(value).toLocaleString()
            },
            {
                title: '操作',
                dataIndex: 'id',
                key: 'id',
                render: (value, record) => <Space>
                    <Typography.Link onClick={this.showUpdateModal.bind(this, record.id)}>修改</Typography.Link>
                    <Popconfirm title={`你是否確定要刪除 ${record.name}?`} onConfirm={this.handleDelete.bind(this, record.id)} okText="是" cancelText="否">
                        <Typography.Link>刪除</Typography.Link>
                    </Popconfirm>
                </Space>
            }
        ]
        return (
            <Spin spinning={this.state.loading}>
                <h1>帳號管理</h1>
                <Tabs defaultActiveKey="accounts">
                    <Tabs.TabPane tab="帳號" key="accounts">
                        <Row justify="end" style={{ marginBottom: 10 }}>
                            <Col>
                                <Space>
                                    <Button type="primary" onClick={this.showAddModal}>新增</Button>
                                </Space>
                            </Col>
                        </Row>
                        <Table columns={columns} dataSource={this.state.results} size="small" scroll={{ x: 'max-content' }} />
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="邀請" key="invitations">
                        <InvitationPanel />
                    </Tabs.TabPane>
                </Tabs>
                <Modal
                    title={this.state.modalType === 'store' ? "新增" : "修改"}
                    visible={this.state.modalVisible}
                    onOk={this.handleOk}
                    onCancel={this.hideModal}
                    okButtonProps={{ disabled: this.state.modalLoading }}
                >
                    <Spin spinning={this.state.modalLoading}>
                        <Form layout="vertical" ref={this.form} onFinish={this.handleFinish}>
                            <Form.Item label="名稱" name="name">
                                <Input />
                            </Form.Item>
                            <Form.Item label="E-mail" name="email">
                                <Input />
                            </Form.Item>
                            <Form.Item
                                label="角色"
                                name="role"
                                rules={[{ required: true, message: '請選擇角色' }]}
                            >
                                <Select options={ROLE_OPTIONS} />
                            </Form.Item>
                            <Form.Item label="部門" name="department_ids">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="未設定"
                                    options={this.state.departmentOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                            <Form.Item label="密碼" name="password">
                                <Input.Password />
                            </Form.Item>
                        </Form>
                    </Spin>
                </Modal>
            </Spin>
        )
    }
}

export default UserPage